import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import axiosRetry from "axios-retry";
import { HttpConfig } from "@fitko/fit-connect";
import { HttpService } from "@fitko/fit-connect";
import { loggerService } from "@fitko/fit-connect";

/**
 * DISCLAIMER: This class is a fork of class "ApiService" from the javascript-sdk
 * (see https://git.fitko.de/fit-connect/sdk-javascript/-/blob/main/src/core/apiService.ts?ref_type=heads ).
 * As long as https://git.fitko.de/fit-connect/planning/-/issues/4087 is not addressed upstream, we have to
 * keep using this fork!
 *
 * ApiService is a core service that handles HTTP communication with the FIT-Connect backend.
 * It implements the HttpService interface and provides methods for making HTTP requests with automatic retry logic.
 *
 * @remarks
 * The service uses axios for HTTP requests and includes:
 * - Automatic retry on network failures
 * - Configurable timeout
 * - Comprehensive error logging
 * - Type-safe responses
 *
 * @example
 * ```typescript
 * const apiService = new ApiService({ timeoutInMs: 5000, retries: 3 });
 * const response = await apiService.get<MyResponseType>('https://api.example.com/data');
 * ```
 */
export class ForkedApiService implements HttpService {
  /** Default timeout in milliseconds. 0 means no timeout. */
  static readonly DEFAULT_TIMEOUT: number = 0;
  private readonly http: AxiosInstance;

  /**
   * Creates a new instance of ApiService.
   *
   * @param httpConfig - Optional configuration for HTTP requests
   * @param httpConfig.timeoutInMs - Request timeout in milliseconds
   * @param httpConfig.retries - Number of retry attempts for failed requests
   */
  constructor(httpConfig?: HttpConfig) {
    axiosRetry(axios, {
      retries: httpConfig?.retries,
      retryCondition: (error) => axios.isAxiosError(error) && !error.response,
    });
    this.http = axios.create({
      timeout: httpConfig?.timeoutInMs ?? ForkedApiService.DEFAULT_TIMEOUT,
      withCredentials: true, // TODO: add support for this in the javascript SDK
    });
  }

  /**
   * Performs a GET request to the specified URL.
   *
   * @param url - The URL to send the GET request to
   * @param additionalHeaders - Optional additional headers to include in the request
   * @returns A Promise that resolves to the response data of type T
   * @throws Will throw an error if the request fails
   *
   * @example
   * ```typescript
   * const data = await apiService.get<MyDataType>('https://api.example.com/data',
   *   new Map([['Authorization', 'Bearer token']]));
   * ```
   */
  public get<T>(
    url: string,
    additionalHeaders?: Map<string, string>,
  ): Promise<T> {
    return this.executeCall({
      method: "GET",
      url: url,
      headers: {
        ...defaultHeader(),
        ...(additionalHeaders
          ? Object.fromEntries(additionalHeaders.entries())
          : {}),
      },
    });
  }

  /**
   * Performs a POST request with multipart/form-data to the specified URL.
   *
   * @param url - The URL to send the POST request to
   * @param formData - The FormData object containing form fields and files
   * @param additionalHeaders - Optional additional headers to include in the request
   * @returns A Promise that resolves to the response data of type T
   * @throws Will throw an error if the request fails
   *
   * @example
   * ```typescript
   * const formData = new FormData();
   * formData.append('data', JSON.stringify(dataObject));
   * formData.append('file', fileObject);
   * const response = await apiService.postMultipart<MyResponseType>(
   *   'https://api.example.com/upload',
   *   formData
   * );
   * ```
   */
  public post<T>(
    url: string,
    formData: FormData,
    additionalHeaders?: Map<string, string>,
  ): Promise<T> {
    return this.executeCall({
      method: "POST",
      url: url,
      data: formData,
      headers: {
        ...defaultHeader("multipart/form-data"),
        ...(additionalHeaders
          ? Object.fromEntries(additionalHeaders.entries())
          : {}),
      },
      withCredentials: true,
    });
  }

  /**
   * Performs a PUT request to the specified URL.
   *
   * @param url - The URL to send the PUT request to
   * @param payload - The data to send in the request body
   * @param additionalHeaders - Optional additional headers to include in the request
   * @returns A Promise that resolves to the response data of type T
   * @throws Will throw an error if the request fails
   *
   * @example
   * ```typescript
   * const response = await apiService.put<MyResponseType>(
   *   'https://api.example.com/data/123',
   *   { key: 'updated value' }
   * );
   * ```
   */
  public put<T>(
    url: string,
    payload: string | object,
    additionalHeaders?: Map<string, string>,
  ): Promise<T> {
    return this.executeCall({
      method: "PUT",
      url: url,
      data: payload,
      headers: {
        ...defaultHeader(),
        ...(additionalHeaders
          ? Object.fromEntries(additionalHeaders.entries())
          : {}),
      },
    });
  }

  /**
   * Executes an HTTP request with error handling and logging.
   *
   * @param request - The Axios request configuration
   * @returns A Promise that resolves to the response data of type T
   * @throws Will throw an error if the request fails, with detailed error information
   *
   * @private
   */
  private async executeCall<T>(request: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.http.request(request);
      loggerService.logger.info(
        `${request.method} request to ${request.url} returned with ${response.status}`,
      );
      return response.data as T;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          loggerService.logger.error(
            `API request failed with status ${error.response.status}: ${JSON.stringify(
              error.response.data,
            )}`,
          );
        } else if (error.request) {
          loggerService.logger.error(
            `No response from server: ${JSON.stringify(error)}`,
          );
        } else {
          loggerService.logger.error(
            `Invalid request: ${JSON.stringify(error)}`,
          );
        }
      } else {
        loggerService.logger.error(
          `Unknown error: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
      return Promise.reject(
        error instanceof Error ? error : new Error(String(error)),
      );
    }
  }
}

export function defaultHeader(contentType: string = "application/json"): {
  "Content-Type": string;
  Accept: string;
} {
  return {
    "Content-Type": contentType,
    Accept: "application/json",
  };
}
